import { useCallback, useEffect, useRef, useState } from 'react';

type TimeoutHandle = ReturnType<typeof setTimeout>;
type Entry<T> = { v: T };

export function useQueuedPulse<T>(
    item: T,
    delay: number,
    qualifies: (item: T) => boolean
): T | undefined {
    const [value, setValue] = useState<T | undefined>(undefined);

    const queueRef = useRef<Entry<T>[]>([]);
    const activeRef = useRef(false);
    const timerRef = useRef<TimeoutHandle | null>(null);

    // Always use latest delay and predicate without re-triggering enqueue effects.
    const delayRef = useRef(delay);
    useEffect(() => {
        delayRef.current = delay;
    }, [delay]);

    const qualifiesRef = useRef(qualifies);
    useEffect(() => {
        qualifiesRef.current = qualifies;
    }, [qualifies]);

    const clearTimer = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    const startNextRef = useRef<() => void>(() => {});
    startNextRef.current = () => {
        const next = queueRef.current.shift();
        if (!next) {
            // Nothing left: remain undefined and mark idle
            activeRef.current = false;
            setValue(undefined);
            return;
        }

        // Emit immediately
        activeRef.current = true;
        setValue(next.v);

        // After delay, clear back to undefined, then proceed to next queued item
        clearTimer();
        timerRef.current = setTimeout(() => {
            setValue(undefined);
            startNextRef.current();
        }, Math.max(0, delayRef.current));
    };

    useEffect(() => {
        // Enqueue only if it qualifies
        if (!qualifiesRef.current(item)) return;

        queueRef.current.push({ v: item });

        // If idle, begin immediately; otherwise it will be picked up after current delay window.
        if (!activeRef.current) {
            startNextRef.current();
        }
    }, [item]); // intentionally only reacts to item changes

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearTimer();
            queueRef.current = [];
            activeRef.current = false;
        };
    }, [clearTimer]);

    return value;
}
